import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const PrivacyPolicy = () => {
    console.log(`[${new Date().toISOString()}] PrivacyPolicy component render`);
    const { t } = useTranslation();

    // Colors for consistent theming
    const primaryTextColor = "text-silver-light"; // Very light gray/silver
    const secondaryTextColor = "text-silver-medium"; // Medium gray/silver

    return (
        <div className="bg-black min-h-screen pt-24 pb-16">
            <SEO
                title="Privacy Policy | Riviera Tech Solutions"
                description="Read how Riviera Tech Solutions collects, uses, stores, and protects personal data in our Privacy Policy."
                keywords="privacy policy, data protection, personal data policy, GDPR privacy Riviera Tech"
                url="/privacy"
            />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className={`text-4xl font-serif font-medium mb-6 ${primaryTextColor}`}>{t('legal.privacyPolicy.title')}</h1>
                    <p className={`${secondaryTextColor} mb-8`}>{t('legal.lastUpdated')}: {new Date().toLocaleDateString()}</p>

                    <div className="space-y-8">
                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section1.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section1.paragraph1')}</p>
                                <p>{t('legal.privacyPolicy.section1.paragraph2')}</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section2.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section2.paragraph1')}</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section2.types.personal.title')}:</span> {t('legal.privacyPolicy.section2.types.personal.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section2.types.usage.title')}:</span> {t('legal.privacyPolicy.section2.types.usage.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section2.types.cookies.title')}:</span> {t('legal.privacyPolicy.section2.types.cookies.description')}</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section3.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section3.paragraph1')}</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>{t('legal.privacyPolicy.section3.purposes.item1')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item2')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item3')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item4')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item5')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item6')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item7')}</li>
                                    <li>{t('legal.privacyPolicy.section3.purposes.item8')}</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section4.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section4.paragraph1')}</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section5.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section5.paragraph1')}</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.access.title')}:</span> {t('legal.privacyPolicy.section5.rights.access.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.rectification.title')}:</span> {t('legal.privacyPolicy.section5.rights.rectification.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.erasure.title')}:</span> {t('legal.privacyPolicy.section5.rights.erasure.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.restrict.title')}:</span> {t('legal.privacyPolicy.section5.rights.restrict.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.object.title')}:</span> {t('legal.privacyPolicy.section5.rights.object.description')}</li>
                                    <li><span className={primaryTextColor}>{t('legal.privacyPolicy.section5.rights.portability.title')}:</span> {t('legal.privacyPolicy.section5.rights.portability.description')}</li>
                                </ul>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section6.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section6.paragraph1')}</p>
                                <p>{t('legal.privacyPolicy.section6.paragraph2')}</p>
                            </div>
                        </section>

                        <section>
                            <h2 className={`text-2xl font-medium mb-4 ${primaryTextColor}`}>{t('legal.privacyPolicy.section7.heading')}</h2>
                            <div className={`${secondaryTextColor} space-y-4 text-sm leading-relaxed`}>
                                <p>{t('legal.privacyPolicy.section7.paragraph1')}</p>
                                <ul className="list-disc pl-6 space-y-2">
                                    <li>{t('legal.privacyPolicy.section7.contactMethods.email', { email: 'support@rivieratech.solutions' })}</li>
                                    <li>{t('legal.privacyPolicy.section7.contactMethods.contact')}</li>
                                </ul>
                            </div>
                        </section>
                    </div>

                    <div className="mt-10 pt-6 border-t border-gray-800">
                        <p className="text-[11px] uppercase tracking-[0.16em] text-silver-dark mb-2">Related pages</p>
                        <p className="text-xs sm:text-sm text-silver-medium">
                            <Link to="/terms" className="text-silver-dark hover:text-silver-medium transition-colors">Terms of Service</Link>
                            {" · "}
                            <Link to="/cookies" className="text-silver-dark hover:text-silver-medium transition-colors">Cookie Policy</Link>
                            {" · "}
                            <Link to="/eula" className="text-silver-dark hover:text-silver-medium transition-colors">EULA</Link>
                            {" · "}
                            <Link to="/contact" className="text-silver-dark hover:text-silver-medium transition-colors">Contact</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
